import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';
import type { SampleChip } from '@/lib/fixtures/sample_chips';

// TopicChipPicker — P1 step 1 chip + textarea.
// ADDENDUM correction 4: live char counter + onCustomTopicChange callback.
// Min 50 / Max 20000 matches BE topic_too_short / topic_too_long.

export interface TopicChipPickerProps {
  chips: SampleChip[];
  defaultChipId?: string;
  onChipSelected: (chip: SampleChip) => void;
  onCustomTopicChange?: (topic: string) => void;
  onCustomTopicSubmit: (topic: string) => void;
  charLimits?: { min: number; max: number };
  placeholder?: string;
  busy?: boolean;
  disabled?: boolean;
}

const DEFAULT_LIMITS = { min: 50, max: 20000 } as const;

export function TopicChipPicker({
  chips,
  defaultChipId,
  onChipSelected,
  onCustomTopicChange,
  onCustomTopicSubmit,
  charLimits = DEFAULT_LIMITS,
  placeholder,
  busy,
  disabled,
}: TopicChipPickerProps) {
  const { t } = useTranslation();
  const [activeChipId, setActiveChipId] = useState<string | null>(defaultChipId ?? null);
  const [topic, setTopic] = useState('');

  const charCount = topic.trim().length;
  const canSubmit = useMemo(() => {
    return charCount >= charLimits.min && charCount <= charLimits.max && !busy && !disabled;
  }, [charCount, charLimits.min, charLimits.max, busy, disabled]);

  const onChip = (chip: SampleChip) => {
    setActiveChipId(chip.chip_id);
    setTopic('');
    onChipSelected(chip);
  };

  const onText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setTopic(v);
    setActiveChipId(null);
    onCustomTopicChange?.(v);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onCustomTopicSubmit(topic.trim());
  };

  return (
    <section className="px-8 py-8 border border-hairline rounded-md bg-paper" data-testid="topic-chip-picker">
      <Eyebrow tone="vermilion" className="mb-3">
        {t('ahaOnboarding.pickTopic.title')}
      </Eyebrow>
      <p className="text-ink-2 mb-6 leading-relaxed">{t('ahaOnboarding.pickTopic.sub')}</p>

      <div className="flex flex-wrap gap-2 mb-6" role="listbox" aria-label="Sample topics">
        {chips.map((chip) => (
          <button
            key={chip.chip_id}
            type="button"
            role="option"
            aria-selected={activeChipId === chip.chip_id}
            onClick={() => onChip(chip)}
            disabled={busy || disabled}
            className={[
              'px-4 py-2 text-sm border rounded-full transition-colors',
              activeChipId === chip.chip_id
                ? 'border-vermilion bg-vermilion-50/60 text-vermilion-700'
                : 'border-hairline hover:border-vermilion/60 text-ink',
              busy || disabled ? 'opacity-50 cursor-not-allowed' : '',
            ].join(' ')}
            data-testid={`topic-chip-${chip.chip_id}`}
          >
            {t(chip.i18n_key)}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="font-mono text-[10px] tracking-meta uppercase text-ink-2 mb-2 block">
            {t('newSession.label')}
          </span>
          <textarea
            value={topic}
            onChange={onText}
            placeholder={placeholder ?? t('ahaOnboarding.pickTopic.customPlaceholder')}
            rows={5}
            disabled={busy || disabled}
            data-testid="topic-textarea"
            className="w-full px-3 py-2 bg-canvas border border-hairline rounded-md text-sm text-ink focus:outline-none focus:ring-2 focus:ring-vermilion/30 focus:border-vermilion disabled:opacity-60"
            maxLength={charLimits.max}
          />
        </label>
        <div className="flex justify-between items-center">
          <span
            className={[
              'font-mono text-[11px] tracking-meta',
              charCount >= charLimits.min ? 'text-jade-700' : 'text-ink-3',
            ].join(' ')}
            data-testid="char-counter"
          >
            {t('ahaOnboarding.pickTopic.charCounter', { count: charCount })}
          </span>
          <Button type="submit" variant="vermilion" disabled={!canSubmit} loading={busy}>
            {t('ahaOnboarding.pickTopic.submitCta')}
          </Button>
        </div>
      </form>
    </section>
  );
}

export default TopicChipPicker;
